import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_CACHE_ENTRIES = 50; // LRU-ish memory cap

/**
 * Hook professionnel pour la gestion des documents :
 *  - Telechargement avec progression (chunks)
 *  - Cache blob en memoire LRU
 *  - Preview ultra rapide (single-fetch + in-flight dedupe)
 *  - Annulation des fetchs en cours (AbortController)
 *  - Cache-Control intelligent (preview = .blob() atomique, download = stream avec progress)
 */
export function useDocumentManager(token) {
  const [downloads, setDownloads] = useState({});
  const blobCache = useRef(new Map());          // docId -> Blob (LRU)
  const inflight = useRef(new Map());            // docId -> Promise<Blob> (dedupe)
  const abortRefs = useRef(new Map());           // docId -> AbortController

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const _touchCache = (docId, blob) => {
    // LRU : on retire puis on remet en fin (dernier utilise)
    if (blobCache.current.has(docId)) blobCache.current.delete(docId);
    blobCache.current.set(docId, blob);
    while (blobCache.current.size > MAX_CACHE_ENTRIES) {
      const oldestKey = blobCache.current.keys().next().value;
      blobCache.current.delete(oldestKey);
    }
  };

  // Telechargement avec progression (stream reader)
  const downloadWithProgress = useCallback(async (url, docId) => {
    // Annule un eventuel fetch precedent pour ce meme docId
    if (abortRefs.current.has(docId)) {
      try { abortRefs.current.get(docId).abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    abortRefs.current.set(docId, controller);

    setDownloads((prev) => ({ ...prev, [docId]: { progress: 0, status: 'loading' } }));

    try {
      const response = await fetch(url, { headers: getHeaders(), signal: controller.signal, cache: 'no-store' });
      if (!response.ok) {
        // V36.44 — Erreur HTTP claire
        let errMsg = `HTTP ${response.status}`;
        try {
          const errBody = await response.text();
          if (errBody) {
            try {
              const errJson = JSON.parse(errBody);
              errMsg = errJson.detail || errJson.message || errMsg;
            } catch { errMsg = errBody.slice(0, 200) || errMsg; }
          }
        } catch { /* noop */ }
        throw new Error(errMsg);
      }

      const contentLength = parseInt(response.headers.get('Content-Length') || response.headers.get('X-File-Size') || '0', 10);
      const contentType = response.headers.get('Content-Type') || 'application/octet-stream';

      // Si la reponse n'expose pas body.getReader (rare), fallback blob direct
      if (!response.body || !response.body.getReader) {
        const blob = await response.blob();
        _touchCache(docId, blob);
        setDownloads((prev) => ({ ...prev, [docId]: { progress: 100, status: 'done' } }));
        return blob;
      }

      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (contentLength > 0) {
          const progress = Math.min(Math.round((received / contentLength) * 100), 100);
          setDownloads((prev) => ({ ...prev, [docId]: { progress, status: 'loading', received, total: contentLength } }));
        }
      }

      const blob = new Blob(chunks, { type: contentType });
      // V36.44 — Detection blob vide (0 KB) suite a un fichier source corrompu
      if (!blob || blob.size === 0) {
        throw new Error("Le fichier est vide ou corrompu (0 octet). Verifiez le document source.");
      }
      _touchCache(docId, blob);
      setDownloads((prev) => ({ ...prev, [docId]: { progress: 100, status: 'done' } }));

      // Auto-clear status apres 3s
      setTimeout(() => {
        setDownloads((prev) => {
          const next = { ...prev };
          delete next[docId];
          return next;
        });
      }, 3000);

      return blob;
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setDownloads((prev) => ({ ...prev, [docId]: { progress: 0, status: 'error' } }));
      }
      throw err;
    } finally {
      abortRefs.current.delete(docId);
    }
  }, [getHeaders]);

  // Telecharger un document (force le download)
  const downloadDocument = useCallback(async (docId, filename, downloadUrl) => {
    try {
      const url = downloadUrl || `${API_URL}/api/ged/documents/${docId}/download`;
      // Re-use le blob cache si dispo
      let blob = blobCache.current.get(docId);
      if (!blob) {
        blob = await downloadWithProgress(url, docId);
      }
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || `${docId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success(`${filename || 'Document'} telecharge`);
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('Erreur de telechargement');
    }
  }, [downloadWithProgress]);

  // Preview ultra-rapide : blob().then(URL) avec in-flight dedupe + cache LRU
  // On utilise response.blob() (atomique) pour eviter tout conflit de reader.
  const previewDocument = useCallback(async (docId, downloadUrl) => {
    // 1. Cache hit instantane
    if (blobCache.current.has(docId)) {
      const cached = blobCache.current.get(docId);
      _touchCache(docId, cached);
      return URL.createObjectURL(cached);
    }
    // 2. Deduplication des appels parallels
    if (inflight.current.has(docId)) {
      const blob = await inflight.current.get(docId);
      return URL.createObjectURL(blob);
    }
    const url = downloadUrl || `${API_URL}/api/ged/documents/${docId}/download?mode=view`;
    const promise = (async () => {
      // Annule un eventuel preview/download en cours pour ce doc
      if (abortRefs.current.has(docId)) {
        try { abortRefs.current.get(docId).abort(); } catch { /* noop */ }
      }
      const controller = new AbortController();
      abortRefs.current.set(docId, controller);
      try {
        const response = await fetch(url, { headers: getHeaders(), signal: controller.signal, cache: 'no-store' });
        if (!response.ok) {
          // V36.44 — Erreur HTTP claire (404 doc introuvable, 403, etc.)
          let errMsg = `HTTP ${response.status}`;
          try {
            const errBody = await response.text();
            if (errBody) {
              try {
                const errJson = JSON.parse(errBody);
                errMsg = errJson.detail || errJson.message || errMsg;
              } catch { errMsg = errBody.slice(0, 200) || errMsg; }
            }
          } catch { /* noop */ }
          throw new Error(errMsg);
        }
        // .blob() lit l'integralite en 1 fois (atomique) — pas de conflit reader.
        let blob = await response.blob();
        // V36.44 — Detection d'un blob VIDE (0 KB) qui correspond a un fichier
        // tronque cote serveur ou a un upload corrompu. On le signale clairement
        // au lieu de laisser un PDF blanc s'afficher.
        if (!blob || blob.size === 0) {
          throw new Error("Le fichier est vide ou corrompu (0 octet). Verifiez le document source.");
        }
        const ct = response.headers.get('Content-Type') || blob.type || '';
        if (!ct || ct === 'application/octet-stream') {
          blob = new Blob([blob], { type: 'application/pdf' });
        }
        _touchCache(docId, blob);
        return blob;
      } finally {
        abortRefs.current.delete(docId);
        inflight.current.delete(docId);
      }
    })();
    inflight.current.set(docId, promise);
    const blob = await promise;
    return URL.createObjectURL(blob);
  }, [getHeaders]);

  // Telechargement batch
  const downloadBatch = useCallback(async (documents) => {
    const results = [];
    for (const doc of documents) {
      try {
        await downloadDocument(doc.ged_id || doc.id, doc.filename || doc.nom, doc.download_url);
        results.push({ id: doc.ged_id || doc.id, success: true });
      } catch {
        results.push({ id: doc.ged_id || doc.id, success: false });
      }
    }
    const success = results.filter((r) => r.success).length;
    toast.success(`${success}/${documents.length} documents telecharges`);
    return results;
  }, [downloadDocument]);

  // Pre-fetch (warm cache) — utile pour les listes
  const prefetchDocument = useCallback(async (docId, downloadUrl) => {
    if (blobCache.current.has(docId) || inflight.current.has(docId)) return;
    try { await previewDocument(docId, downloadUrl); } catch { /* silencieux */ }
  }, [previewDocument]);

  const clearCache = useCallback(() => {
    blobCache.current.clear();
    inflight.current.clear();
  }, []);

  return {
    downloads,
    downloadDocument,
    previewDocument,
    prefetchDocument,
    downloadBatch,
    clearCache,
  };
}
